import { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { api, refreshTokens, tokenStore } from '../api/client';
import type { AdminUser } from '../api/types';

interface AuthState {
  user: AdminUser | null;
  permissions: Set<string>;
  loading: boolean;
  login(email: string, password: string): Promise<void>;
  logout(): Promise<void>;
  /** UI hint only — the API enforces every permission. */
  can(...perms: string[]): boolean;
}

const Ctx = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AdminUser | null>(null);
  const [permissions, setPermissions] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);

  const loadMe = useCallback(async () => {
    const r = await api.get('/admin/me');
    setUser(r.data.user);
    setPermissions(new Set(r.data.permissions));
  }, []);

  const reset = useCallback(() => {
    setUser(null);
    setPermissions(new Set());
  }, []);

  useEffect(() => {
    tokenStore.onLoggedOut(reset);
    (async () => {
      try {
        if (tokenStore.hasRefresh() && (await refreshTokens())) await loadMe();
      } catch {
        reset();
      } finally {
        setLoading(false);
      }
    })();
  }, [loadMe, reset]);

  const value = useMemo<AuthState>(
    () => ({
      user,
      permissions,
      loading,
      async login(email, password) {
        const r = await api.post('/auth/admin/login', { email, password });
        tokenStore.set(r.data.tokens.accessToken, r.data.tokens.refreshToken);
        await loadMe();
      },
      async logout() {
        await api.post('/auth/logout').catch(() => undefined);
        tokenStore.clear();
        reset();
      },
      can: (...perms) => permissions.has('*') || perms.every((p) => permissions.has(p)),
    }),
    [user, permissions, loading, loadMe, reset],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useAuth(): AuthState {
  const v = useContext(Ctx);
  if (!v) throw new Error('useAuth outside AuthProvider');
  return v;
}
